import React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import cx from 'classnames';

import Heading from '../Heading';
import Button from '../Button';
import { IconClose } from '../icons/Icons';

export default class Modal extends React.PureComponent {
    static propTypes = {
        /** If true, modal is visible */
        isOpen: PropTypes.bool,

        /** The modal title. You are strongly encouraged to use this prop. If
         * not provided you must provide your own title and include aria
         * attributes to link the modal's describedBy to your custom heading */
        heading: PropTypes.node,

        /** Contents of the modal */
        children: PropTypes.node.isRequired,

        /** Function called when user clicks the overlay, the close button or
         * otherwise triggers an action that should close the modal */
        onRequestClose: PropTypes.func.isRequired,

        /** Sets the width of the modal. If set to full, the height will also
         * match the height of the viewport */
        size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'xxl', 'full']),

        /** If true, will render a 'X' close button in the upper right corner */
        shouldShowCloseButton: PropTypes.bool,

        /** Requires you use the ModalActions component in the children. If
         * true, the modal actions bar will be fixed at the bottom in mobile,
         * making it easier for users to find the action buttons without
         * scrolling.  */
        shouldActionsStickInMobile: PropTypes.bool,

        overlayClassName: PropTypes.string,
    };

    static defaultProps = {
        isOpen: false,
        onRequestClose: () => {},
        size: 'md',
        shouldShowCloseButton: true,
        shouldActionsStickInMobile: false,
        overlayClassName: '',
    };

    componentDidMount() {
        ReactModal.setAppElement('#recApp');
    }

    componentDidUpdate() {
        const { isOpen } = this.props;
        const recApp = document.getElementById('recApp');

        if (isOpen && recApp) {
            recApp.style.height = '100vh';
            recApp.style.minHeight = '100vh';
            recApp.style.maxHeight = '100vh';
            recApp.style.overflow = 'hidden';
        } else {
            this.removeRecAppStyle();
        }
    }

    componentWillUnmount() {
        this.removeRecAppStyle();
    }

    removeRecAppStyle = () => {
        const recApp = document.getElementById('recApp');

        if (recApp) {
            recApp.style.height = '';
            recApp.style.minHeight = '';
            recApp.style.maxHeight = '';
            recApp.style.overflow = '';
        }
    };

    handleCloseClick = (e) => {
        if(e?.preventDefault) {
            e.preventDefault();
        }

        if (this.props.onRequestClose) {
            this.props.onRequestClose();
        }
    };

    render() {
        const {
            isOpen,
            heading,
            children,
            onRequestClose,
            size,
            shouldShowCloseButton,
            shouldActionsStickInMobile,
            overlayClassName,
            ...rest
        } = this.props;

        return (
            <ReactModal
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                shouldCloseOnOverlayClick
                aria={{
                    labelledby: 'modal-heading',
                }}
                overlayClassName={{
                    base: `sarsa-modal-overlay ${overlayClassName}`,
                    afterOpen: 'sarsa-modal-overlay-after-open',
                    beforeClose: 'sarsa-modal-overlay-before-close',
                }}
                className={{
                    base: 'sarsa-modal',
                    afterOpen: 'sarsa-modal-after-open',
                    beforeClose: 'sarsa-modal-before-close',
                }}
                {...rest}
            >
                <div className="sarsa-modal-content-cell">
                    <div className="sarsa-modal-content-cell-sizer">
                        <div
                            className={cx('sarsa-modal-content-sizer', {
                                'sarsa-modal-size-sm': size === 'sm',
                                'sarsa-modal-size-md': size === 'md',
                                'sarsa-modal-size-lg': size === 'lg',
                                'sarsa-modal-size-xl': size === 'xl',
                                'sarsa-modal-size-xxl': size === 'xxl',
                                'sarsa-modal-size-full': size === 'full',
                            })}
                        >
                            <div className="sarsa-modal-content">
                                <div
                                    className={cx('sarsa-modal-content-body', {
                                        full: size === 'full',
                                        'sticky-actions': shouldActionsStickInMobile,
                                    })}
                                >
                                    {shouldShowCloseButton && (
                                        <Button
                                            className="sarsa-modal-close-button"
                                            appearance="subtle"
                                            size={size === 'full' ? 'lg' : 'md'}
                                            aria-label="Close modal"
                                            iconBeforeElement={<IconClose />}
                                            onClick={this.handleCloseClick}
                                        />
                                    )}
                                    {heading && (
                                        <Heading
                                            id="modal-heading"
                                            headingLevel={1}
                                            appearance="h4"
                                            className="sarsa-modal-heading"
                                        >
                                            {heading}
                                        </Heading>
                                    )}

                                    {children}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ReactModal>
        );
    }
}
